import {
  type APIMessage,
  can,
  ChannelType,
  composerState,
  MessageType,
  PermissionFlags,
  THREAD_CHANNEL_TYPES,
} from '@alternativamais/chat-core';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Avatar } from '../../componentes/Avatar';
import { avisarErro } from '../../componentes/Avisos';
import {
  Botao,
  Flutuante,
  Modal,
  type Posicao,
} from '../../componentes/Flutuante';
import { Icone } from '../../componentes/Icone';
import { Markdown } from '../../componentes/Markdown';
import { useAtender } from '../../chat/atalhos';
import { useCliente, useEstado } from '../../chat/contexto';
import { useNome } from '../../chat/ganchos';
import {
  type ControleCompositor,
  Compositor,
  type Resposta,
} from './compositor/Compositor';
import { Digitando } from './Digitando';
import { type ControleLista, ListaDeMensagens } from './ListaDeMensagens';
import { type AcoesDoCanal, ContextoCanal } from './mensagem/contexto';
import { NomeDoAutor } from './mensagem/Mensagem';
import { type EmojiEscolhido, SeletorEmoji } from './SeletorEmoji';

/**
 * Uma conversa: mensagens, compositor e "digitando" de um canal, thread ou
 * DM — a do meio da tela e a do painel de thread. Cuida de responder,
 * editar, apagar, soltar arquivo e do seletor de emoji das mensagens.
 */
export function Conversa({
  canalId,
  mensagemInicial,
  criarThread,
  antesDoCompositor,
  textoDoCompositor,
}: {
  canalId: string;
  mensagemInicial?: string;
  /** Só na conversa do canal (thread não tem thread). */
  criarThread?: (mensagem: APIMessage) => void;
  /** Faixa acima do compositor (thread arquivada, por exemplo). */
  antesDoCompositor?: ReactNode;
  /** Texto de exemplo do compositor, quando não é "Conversar em #canal". */
  textoDoCompositor?: string;
}) {
  const cliente = useCliente();
  const canal = useEstado((estado) => estado.channels[canalId]);
  const nomeDaPessoa = useNome(
    canal?.recipients?.[0]?.id ?? '',
    null,
    canal?.recipients?.[0],
  );
  const nomeDoGrupo =
    canal?.type === ChannelType.GROUP_DM && !canal.name
      ? (canal.recipients ?? [])
          .map((pessoa) => pessoa.global_name ?? pessoa.username)
          .join(', ')
      : (canal?.name ?? '');
  const guildId = canal?.guild_id ?? null;
  const [resposta, setResposta] = useState<Resposta | null>(null);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [destacadaId, setDestacadaId] = useState<string | null>(null);
  const [emoji, setEmoji] = useState<{
    ancora: Posicao;
    aoEscolher: (emoji: EmojiEscolhido) => void;
  } | null>(null);
  const [apagando, setApagando] = useState<APIMessage | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const profundidade = useRef(0);
  const lista = useRef<ControleLista | null>(null);
  const compositor = useRef<ControleCompositor | null>(null);

  const destacar = useCallback((id: string) => {
    setDestacadaId(id);
    window.setTimeout(
      () => setDestacadaId((atual) => (atual === id ? null : atual)),
      2_000,
    );
  }, []);

  const apagar = useCallback(
    (mensagem: APIMessage, semConfirmar = false) => {
      if (semConfirmar) {
        cliente
          .deleteMessage(mensagem.channel_id, mensagem.id)
          .catch(avisarErro);
        return;
      }
      setApagando(mensagem);
    },
    [cliente],
  );

  // Menu do membro ("Mencionar"): insere no compositor desta conversa.
  useEffect(() => {
    const inserir = (evento: Event) => {
      const { canalId: alvo, texto } = (
        evento as CustomEvent<{ canalId: string; texto: string }>
      ).detail;
      if (alvo === canalId) compositor.current?.inserir(texto);
    };
    window.addEventListener('chat:inserir', inserir);
    return () => window.removeEventListener('chat:inserir', inserir);
  }, [canalId]);

  const acoes = useMemo<AcoesDoCanal>(
    () => ({
      canalId,
      guildId,
      responder: (mensagem) => {
        setEditandoId(null);
        setResposta({
          mensagem,
          mencionar: mensagem.author.id !== cliente.store.getState().me?.id,
        });
      },
      editandoId,
      editar: (id) => {
        setEditandoId(id);
        if (!id) compositor.current?.focar();
      },
      irPara: (id) => lista.current?.irPara(id),
      destacadaId,
      abrirEmoji: (ancora, aoEscolher) => setEmoji({ ancora, aoEscolher }),
      apagar,
      criarThread,
      mencionar: (texto) => compositor.current?.inserir(texto),
    }),
    [canalId, guildId, editandoId, destacadaId, apagar, cliente, criarThread],
  );

  // Ctrl+Home: a primeira mensagem do canal (só na conversa principal).
  const raiz = useRef<HTMLElement>(null);
  useAtender('ir-inicio', () => {
    if (document.querySelector('main[data-conversa]') !== raiz.current) return;
    cliente.api
      .messages(canalId, { after: '0', limit: 1 })
      .then((lista) => {
        const primeira = lista[lista.length - 1];
        if (primeira) acoes.irPara(primeira.id);
      })
      .catch(avisarErro);
  });

  // Seta para cima no compositor vazio: edita a última mensagem minha.
  const editarUltima = useCallback(() => {
    const estado = cliente.store.getState();
    const ids = estado.messages[canalId]?.ids ?? [];
    for (
      let indice = ids.length - 1;
      indice >= Math.max(0, ids.length - 50);
      indice--
    ) {
      const mensagem = estado.messages[canalId].byId[ids[indice]];
      if (
        mensagem.author.id === estado.me?.id &&
        (mensagem.type === MessageType.DEFAULT ||
          mensagem.type === MessageType.REPLY)
      ) {
        setEditandoId(mensagem.id);
        lista.current?.irPara(mensagem.id);
        return;
      }
    }
  }, [cliente, canalId]);

  const podeSoltar = useEstado((estado) => {
    const composto = composerState(estado, canalId);
    return (
      composto.canSend && can(estado, canalId, PermissionFlags.ATTACH_FILES)
    );
  });

  if (!canal) return null;
  const dm = canal.type === ChannelType.DM;
  // Como o design: "#canal" só em canal; thread e grupo pelo nome; DM pela pessoa.
  const semCerquilha =
    canal.type === ChannelType.GROUP_DM || THREAD_CHANNEL_TYPES.has(canal.type);
  const nomeNoCompositor = dm
    ? { tipo: 'dm' as const, nome: nomeDaPessoa }
    : {
        tipo: 'canal' as const,
        nome: canal.name ?? '',
        texto:
          textoDoCompositor ??
          (semCerquilha ? `Conversar em ${nomeDoGrupo}` : undefined),
      };
  const comArquivos = (evento: React.DragEvent) =>
    [...evento.dataTransfer.types].includes('Files');

  return (
    <ContextoCanal.Provider value={acoes}>
      <main
        ref={raiz}
        data-conversa
        className="relative flex min-w-0 grow flex-col"
        onDragEnter={(evento) => {
          if (!comArquivos(evento) || !podeSoltar) return;
          profundidade.current++;
          setArrastando(true);
        }}
        onDragLeave={(evento) => {
          if (!comArquivos(evento) || !podeSoltar) return;
          profundidade.current = Math.max(0, profundidade.current - 1);
          if (!profundidade.current) setArrastando(false);
        }}
        onDragOver={(evento) => {
          if (comArquivos(evento) && podeSoltar) evento.preventDefault();
        }}
        onDrop={(evento) => {
          if (!comArquivos(evento) || !podeSoltar) return;
          evento.preventDefault();
          profundidade.current = 0;
          setArrastando(false);
          const arquivos = [...evento.dataTransfer.files];
          if (!arquivos.length) return;
          if (evento.shiftKey) {
            cliente
              .sendMessage(canalId, {
                content: '',
                files: arquivos.map((arquivo) => ({
                  name: arquivo.name,
                  data: arquivo,
                })),
              })
              .catch(() => undefined);
          } else {
            compositor.current?.adicionarArquivos(arquivos);
          }
        }}
      >
        <ListaDeMensagens
          canalId={canalId}
          guildId={guildId}
          controle={lista}
          aoDestacar={destacar}
          mensagemInicial={mensagemInicial}
        />
        {antesDoCompositor}
        <Compositor
          canalId={canalId}
          guildId={guildId}
          nomeDoCanal={nomeNoCompositor}
          resposta={resposta}
          aoMudarResposta={setResposta}
          aoEditarUltima={editarUltima}
          controle={compositor}
        />
        <Digitando canalId={canalId} guildId={guildId} />
        {arrastando && (
          <SoltarArquivos
            nome={
              dm
                ? `@${canal.recipients?.[0]?.username ?? ''}`
                : canal.type === ChannelType.GROUP_DM
                  ? canal.name || 'o grupo'
                  : `#${canal.name}`
            }
          />
        )}
      </main>
      {emoji && (
        <Flutuante
          ancora={emoji.ancora}
          aoFechar={() => setEmoji(null)}
          rotulo="Seletor de emoji"
          alinhar="fim"
        >
          <SeletorEmoji
            canalId={canalId}
            aoEscolher={(escolhido) => {
              setEmoji(null);
              emoji.aoEscolher(escolhido);
            }}
          />
        </Flutuante>
      )}
      {apagando && (
        <ConfirmarApagar
          mensagem={apagando}
          aoFechar={() => setApagando(null)}
        />
      )}
    </ContextoCanal.Provider>
  );
}

/** Arrastando arquivo por cima da conversa (C25). */
function SoltarArquivos({ nome }: { nome: string }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-[rgba(5,6,8,.78)]">
      <div
        role="status"
        className="h-[260px] w-[440px] max-w-[90%] rounded-2xl bg-destaque p-2 shadow-[0_12px_32px_rgba(0,0,0,.45)]"
      >
        <div className="flex h-full flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-dashed border-destaque-texto px-9 text-center text-destaque-texto">
          <Icone nome="enviar-arquivo" tamanho={48} />
          <p className="mt-1 mb-0 font-titulo text-2xl leading-[30px] font-bold">
            Enviar para {nome}
          </p>
          <p className="m-0 text-sm leading-normal text-[#3A2A12]">
            Até 50 MB por arquivo. Segure Shift para enviar direto, sem legenda.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Apagar mensagem (C23): prévia, dica do Shift e o aviso de moderação. */
function ConfirmarApagar({
  mensagem,
  aoFechar,
}: {
  mensagem: APIMessage;
  aoFechar: () => void;
}) {
  const cliente = useCliente();
  const minha = useEstado((estado) => estado.me?.id === mensagem.author.id);
  const guildId = useEstado(
    (estado) => estado.channels[mensagem.channel_id]?.guild_id ?? null,
  );
  const [apagando, setApagando] = useState(false);
  const confirmar = async () => {
    setApagando(true);
    try {
      await cliente.deleteMessage(mensagem.channel_id, mensagem.id);
      aoFechar();
    } catch (erro) {
      avisarErro(erro);
      setApagando(false);
    }
  };
  return (
    <Modal
      titulo="Apagar mensagem"
      descricao="Tem certeza? Não dá para desfazer."
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            tipo="perigo"
            data-foco-inicial
            onClick={() => void confirmar()}
            disabled={apagando}
          >
            Apagar
          </Botao>
        </>
      }
    >
      <figure className="m-0 flex gap-3 rounded-lg bg-chat p-3">
        <Avatar
          pessoa={mensagem.author}
          nome={mensagem.author.global_name ?? mensagem.author.username}
        />
        <div className="flex min-w-0 grow flex-col gap-0.5">
          <NomeDoAutor autor={mensagem.author} guildId={guildId} />
          {mensagem.content && (
            <Markdown
              texto={mensagem.content}
              guildId={guildId}
              className="line-clamp-4 text-[15px] leading-[1.45]"
            />
          )}
          {mensagem.attachments.map((anexo) => (
            <span
              key={anexo.id}
              className="mt-1 flex items-center gap-1.5 text-[13px] text-texto-3"
            >
              <Icone nome="clipe" tamanho={14} />
              {anexo.filename}
            </span>
          ))}
        </div>
      </figure>
      <p className="m-0 text-xs text-texto-3">
        Dica: segure{' '}
        <kbd className="font-sans font-semibold text-texto-2">Shift</kbd> ao
        clicar em Apagar (no menu) para pular esta confirmação.
      </p>
      {!minha && (
        <div className="flex items-center gap-2.5 rounded-lg bg-[rgba(125,185,255,.06)] px-3 py-2.5 text-[13px] text-texto-2">
          <Icone nome="escudo" tamanho={18} className="text-link" />
          Como moderador, a exclusão fica no registro de auditoria.
        </div>
      )}
    </Modal>
  );
}
